import * as d3 from 'd3'
import { useMemo, useRef } from 'react'

import { useChartDimensions } from '@/components/ui/chart'
import { chartColors, semanticColors } from '@/utils'

const INCOME_SERIES = [
  8200, 9100, 8500, 10200, 9800, 11400, 11000, 12800, 12200, 13600, 13100,
  14400,
]

const INCOME_PADDING = { top: 6, right: 4, bottom: 6, left: 4 }

export const IncomePreview = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { width, height } = useChartDimensions(
    containerRef as React.RefObject<HTMLDivElement>,
  )

  const chart = useMemo(() => {
    if (!width || !height) return null

    const xScale = d3
      .scaleLinear()
      .domain([0, INCOME_SERIES.length - 1])
      .range([INCOME_PADDING.left, width - INCOME_PADDING.right])

    const minValue = d3.min(INCOME_SERIES) ?? 0
    const maxValue = d3.max(INCOME_SERIES) ?? 1
    const yScale = d3
      .scaleLinear()
      .domain([minValue * 0.85, maxValue * 1.08])
      .range([height - INCOME_PADDING.bottom, INCOME_PADDING.top])

    const lineGenerator = d3
      .line<number>()
      .x((_, index) => xScale(index))
      .y((value) => yScale(value))
      .curve(d3.curveMonotoneX)

    const areaGenerator = d3
      .area<number>()
      .x((_, index) => xScale(index))
      .y0(height - INCOME_PADDING.bottom)
      .y1((value) => yScale(value))
      .curve(d3.curveMonotoneX)

    const points = INCOME_SERIES.map((value, index) => ({
      cx: xScale(index),
      cy: yScale(value),
    }))

    const gridLines = yScale
      .ticks(3)
      .map((tick) => yScale(tick))
      .filter(
        (y) => y > INCOME_PADDING.top && y < height - INCOME_PADDING.bottom,
      )

    return {
      line: lineGenerator(INCOME_SERIES) ?? '',
      area: areaGenerator(INCOME_SERIES) ?? '',
      points,
      gridLines,
    }
  }, [width, height])

  return (
    <div ref={containerRef} className="h-full w-full">
      {chart && (
        <svg width={width} height={height} aria-hidden>
          <title>Income trend preview</title>
          {chart.gridLines.map((y) => (
            <line
              key={y}
              x1={0}
              x2={width}
              y1={y}
              y2={y}
              stroke={chartColors.gray}
              strokeWidth={0.5}
              strokeDasharray="2 3"
              opacity={0.3}
            />
          ))}
          <path d={chart.area} fill={semanticColors.income} opacity={0.14} />
          <path
            d={chart.line}
            stroke={semanticColors.income}
            strokeWidth={1.75}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {chart.points.map((point) => (
            <circle
              key={`${point.cx}-${point.cy}`}
              cx={point.cx}
              cy={point.cy}
              r={2}
              fill="white"
              stroke={semanticColors.income}
              strokeWidth={1.25}
            />
          ))}
        </svg>
      )}
    </div>
  )
}
